/** Today's rule decisions, in order — the audit trail the operator asked for.
 *
 * Operator, 2026-08-21: *"Why are not maintaining logs. If we have logs then
 * we can easily trace."* That day three refusals had to be reconstructed from
 * chart screenshots, because the engine logged only entries and exits.
 *
 * The log lines shipped first and go to the container log — which every deploy
 * destroys, which is exactly when someone wants to read yesterday's refusals.
 * This reads the durable copy instead.
 *
 * ⚠️ Rows are TRANSITIONS, not samples. The engine records a channel only when
 * its verdict CHANGES, so a gate refusing for an hour appears once and a quiet
 * hour appears not at all. An empty trail means "nothing changed", NEVER "the
 * engine was down" — the status card's tick timestamp answers that, and this
 * panel says so rather than letting silence be read as either.
 */

import { type ReactElement } from "react";
import { type EmaTrendDecision } from "@/lib/emaTrendApi";

/** Which rule spoke, when the caller gives no `labelOf` of its own. The
 * channel prefix is the rule; anything after the dot is which part of it. */
function ruleOf(channel: string): string {
  return channel.split(".")[0] ?? channel;
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  });
}

/** The trail's presentation, shared by any engine recording decision rows —
 * hence `labelOf`, which names the channels the caller writes. */
export function Trail({
  rows,
  loading,
  labelOf = ruleOf,
}: Readonly<{
  rows: EmaTrendDecision[];
  loading: boolean;
  labelOf?: (channel: string) => string;
}>): ReactElement {
  if (loading) {
    return <p className="text-xs text-ink-muted">Loading the session&apos;s trail…</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="text-xs text-ink-muted">
        Nothing recorded for today. Decisions are stored when a verdict CHANGES, so this means no
        rule changed its mind — not that the engine is idle. Check the last tick time on the status
        card for that.
      </p>
    );
  }
  return (
    <ol className="flex flex-col gap-1">
      {rows.map((d, i) => (
        <li
          key={`${d.at}-${d.channel}-${String(i)}`}
          className="flex gap-2 rounded-ctl bg-surface-2 px-2 py-1 text-[11px]"
        >
          <span className="shrink-0 font-mono text-ink-muted">{timeOf(d.at)}</span>
          <span className="shrink-0 font-medium text-accent">{labelOf(d.channel)}</span>
          <span className="text-ink-secondary">{d.message}</span>
        </li>
      ))}
    </ol>
  );
}
